import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { organizationApi } from 'src/api/organizationApi';
import { pimApi } from 'src/api/pimApi';
import {
  parseSubscriptionsList,
  subscriptionsApi,
} from 'src/api/subscriptionsApi';
import { userApi } from 'src/api/userApi';
import SubHeader from 'src/components/SubHeader';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import useGenericStyles from 'src/styles/useGenericStyles';

const STATUS_COLORS: Record<string, { text: string; background: string }> = {
  active: {
    text: '#10B981',
    background: '#D1FAE5',
  },
  'expiring soon': {
    text: '#F59E0B',
    background: '#FEF3C7',
  },
  expired: {
    text: '#EF4444',
    background: '#FEE2E2',
  },
};

const uniqueIds = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.map((v) => String(v || '').trim()).filter(Boolean)));

const formatPersonName = (user: any): string => {
  if (!user) return '';
  const first = user?.firstName?.trim() || '';
  const last = user?.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || user?.name || user?.email || '';
};

/** Same naming as Create Subscription product picker. */
const productLabelFromRecord = (product: any): string => {
  const template = product?.productTemplateObject || {};
  return String(
    template?.Name ||
    template?.name ||
    product?.name ||
    product?.productName ||
    product?.label ||
    product?.sku ||
    '',
  ).trim();
};

const mapSubscriptionRow = (
  row: any,
  names: {
    products: Record<string, string>;
    clients: Record<string, string>;
    users: Record<string, string>;
  },
) => {
  const products = Array.isArray(row?.products) ? row.products : [];
  const productLabels = products
    .map((p: any) => {
      const id = String(p?.productId || '').trim();
      const name =
        p?.productName ||
        p?.packageName ||
        p?.name ||
        p?.product?.name ||
        p?.package?.name ||
        names.products[id] ||
        id;
      const qty = Number(p?.numberOfLicenses) || 0;
      if (!name) return '';
      return qty > 0 ? `${name} (${qty})` : name;
    })
    .filter(Boolean);

  const licensesFromProducts = products.reduce(
    (sum: number, p: any) => sum + (Number(p?.numberOfLicenses) || 0),
    0,
  );

  const billingCycle =
    row?.billingCycle || row?.subscriptionType || row?.type || '';

  const clientId = String(row?.clientId || row?.client?.id || '').trim();
  const clientName =
    row?.client?.name ||
    row?.clientName ||
    names.clients[clientId] ||
    '';

  const createdById = String(
    row?.createdBy?.id ||
    (typeof row?.createdBy === 'string' ? row.createdBy : '') ||
    '',
  ).trim();
  const createdByName =
    row?.createdBy?.name ||
    formatPersonName(row?.createdBy) ||
    row?.createdByName ||
    names.users[createdById] ||
    '';

  return {
    ...row,
    id: row?.id || row?._id,
    subscriptionOrderNumber:
      row?.subscriptionOrderNumber || row?.orderNumber || 'N/A',
    customerReference: row?.customerReference,
    billingCycle:
      typeof billingCycle === 'string'
        ? billingCycle.replace(/_/g, ' ').toLowerCase()
        : billingCycle,
    productName:
      row?.productName ||
      (productLabels.length ? productLabels.join(', ') : undefined),
    numberOfLicenses:
      row?.numberOfLicenses ??
      (licensesFromProducts > 0 ? licensesFromProducts : undefined),
    client: clientName || undefined,
    createdBy: createdByName || undefined,
    startDate: row?.startDate,
    endDate: row?.endDate,
    status: row?.status,
  };
};

type NameCache = {
  products: Map<string, string>;
  clients: Map<string, string>;
  users: Map<string, string>;
  productsLoaded: boolean;
  clientsParentId: string | null;
};

/**
 * FE cannot SQL-JOIN across services. Prefer BE-enriched names on the list;
 * otherwise enrich with: 1× product+package catalogue, 1× child orgs,
 * plus CASES user lookups only for creators still missing a name.
 */
async function resolveNameMaps(
  items: any[],
  cache: NameCache,
  opts: { resellerOrgId?: string; currentUserId?: string; currentUserName?: string },
): Promise<{
  products: Record<string, string>;
  clients: Record<string, string>;
  users: Record<string, string>;
}> {
  const { resellerOrgId, currentUserId, currentUserName } = opts;

  if (currentUserId && currentUserName) {
    cache.users.set(currentUserId, currentUserName);
  }

  // Seed from BE-enriched creators so we skip CASES GET /user/{id}.
  items.forEach((row) => {
    const createdBy = row?.createdBy;
    const id = String(
      typeof createdBy === 'string' ? createdBy : createdBy?.id || '',
    ).trim();
    if (!id || cache.users.has(id)) return;
    const name =
      (typeof createdBy === 'object' && createdBy
        ? String(createdBy?.name || '').trim() || formatPersonName(createdBy)
        : '') ||
      String(row?.createdByName || '').trim();
    if (name) cache.users.set(id, name);
  });

  const productIdsNeeded = uniqueIds(
    items.flatMap((row) =>
      (Array.isArray(row?.products) ? row.products : []).map(
        (p: any) => p?.productId,
      ),
    ),
  );

  const clientIdsNeeded = uniqueIds(items.map((row) => row?.clientId));

  const userIdsNeeded = uniqueIds(
    items.map((row) =>
      typeof row?.createdBy === 'string' ? row.createdBy : row?.createdBy?.id,
    ),
  ).filter((id) => !cache.users.has(id));

  await Promise.all([
    (async () => {
      if (productIdsNeeded.length === 0) return;
      // Catalogue once per session: products by domain + PACKAGES.
      if (!cache.productsLoaded) {
        try {
          const response = await pimApi.getProductPackageGroup({
            page: 0,
            pageSize: 1000,
            isActive: true,
          });
          const root = response?.data?.data ?? response?.data ?? response ?? {};
          const groups = Array.isArray(root?.groups)
            ? root.groups
            : Array.isArray(response?.data?.groups)
              ? response.data.groups
              : [];

          groups.forEach((group: any) => {
            const groupItems = Array.isArray(group?.items) ? group.items : [];
            groupItems.forEach((entry: any) => {
              const id = String(entry?.id || entry?._id || '').trim();
              if (!id) return;
              const label = String(entry?.name || entry?.label || '').trim();
              if (label) cache.products.set(id, label);
            });
          });
          cache.productsLoaded = true;
        } catch {
          // Fallback: products-only catalogue if grouped API fails.
          try {
            const response = await pimApi.getAllProducts({
              page: 0,
              pageSize: 1000,
              isActive: true,
            });
            const list = Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response?.data)
                ? response.data
                : [];
            list.forEach((product: any) => {
              const id = String(product?.id || product?._id || '').trim();
              if (!id) return;
              const label = productLabelFromRecord(product);
              if (label) cache.products.set(id, label);
            });
            cache.productsLoaded = true;
          } catch {
            cache.productsLoaded = false;
          }
        }
      }
    })(),
    (async () => {
      if (!resellerOrgId || clientIdsNeeded.length === 0) return;
      if (cache.clientsParentId === resellerOrgId) return;
      try {
        const response = await organizationApi.getByParentId(
          resellerOrgId,
          0,
          200,
        );
        const clients = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        clients.forEach((client: any) => {
          const id = String(client?.id || client?._id || '').trim();
          const name = String(
            client?.name || client?.organizationName || '',
          ).trim();
          if (id && name) cache.clients.set(id, name);
        });
        cache.clientsParentId = resellerOrgId;
      } catch {
        cache.clientsParentId = null;
      }
    })(),
    ...userIdsNeeded.map(async (id) => {
      try {
        const user = await userApi.getById(id);
        cache.users.set(id, formatPersonName(user) || id);
      } catch {
        cache.users.set(id, id);
      }
    }),
  ]);

  return {
    products: Object.fromEntries(cache.products),
    clients: Object.fromEntries(cache.clients),
    users: Object.fromEntries(cache.users),
  };
}

function Subscriptions() {
  const classes = useGenericStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData } = useUserProfile();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({});
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const nameCacheRef = useRef<NameCache>({
    products: new Map(),
    clients: new Map(),
    users: new Map(),
    productsLoaded: false,
    clientsParentId: null,
  });

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await subscriptionsApi.getSubscriptions({
          page: page + 1,
          size: pageSize,
          search: search || undefined,
        });
        if (cancelled) return;
        const { items, total } = parseSubscriptionsList(response);

        const currentUserName = formatPersonName(userData);
        const names = await resolveNameMaps(items, nameCacheRef.current, {
          resellerOrgId: userData?.organization?.id,
          currentUserId: userData?.id,
          currentUserName: currentUserName || undefined,
        });
        if (cancelled) return;

        setData(items.map((row) => mapSubscriptionRow(row, names)));
        setTotalCount(total);
      } catch (error: any) {
        if (cancelled) return;
        setData([]);
        setTotalCount(0);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load subscriptions';
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, userData]);

  const columns = [
    {
      field: 'subscriptionOrderNumber',
      headerName: 'Subscription #',
      sortable: true,
      minWidth: '140px',
    },
    {
      field: 'customerReference',
      headerName: 'Customer Reference',
      sortable: true,
      width: '15%',
      minWidth: '150px',
      render: (value: any): string => value || 'N/A',
    },
    {
      field: 'billingCycle',
      headerName: 'Billing Cycle',
      sortable: true,
      minWidth: '120px',
      render: (value: any): string => value || 'N/A',
    },
    {
      field: 'productName',
      headerName: 'Product / Package',
      sortable: true,
      width: '300px',
      minWidth: '220px',
      render: (value: any): JSX.Element => {
        const label = value || 'N/A';
        return (
          <Box
            sx={{
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={label}>
            {label}
          </Box>
        );
      },
    },
    {
      field: 'numberOfLicenses',
      headerName: 'Licenses',
      sortable: true,
      minWidth: '90px',
      render: (value: any): string => (value != null ? String(value) : 'N/A'),
    },
    {
      field: 'client',
      headerName: 'Client',
      sortable: true,
      width: '20%',
      minWidth: '160px',
      render: (value: any): string => value?.name || value || 'N/A',
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      sortable: true,
      minWidth: '130px',
      render: (value: any): string => value?.name || value || 'N/A',
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      sortable: true,
      minWidth: '120px',
      render: (value: any): string => (value ? formatDate(value) : 'N/A'),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: true,
      minWidth: '120px',
      render: (value: any): string => (value ? formatDate(value) : 'N/A'),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      minWidth: '110px',
      render: (value: any): JSX.Element => {
        const colors =
          STATUS_COLORS[String(value || '').toLowerCase()] || {
            text: '#2196f3',
            background: '#E3F2FD',
          };
        return (
          <Box
            component="span"
            sx={{
              padding: '4px 12px',
              borderRadius: '12px',
              backgroundColor: colors.background,
              color: colors.text,
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
            }}>
            {value || 'N/A'}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>IT22 ERP | Subscriptions</title>
      </Helmet>
      <SubHeader title="Subscriptions" />
      <Box px="30px">
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            mb: '10px',
            gap: '10px',
          }}>
          <SearchBar
            value={search}
            onSearch={handleSearch}
            placeholderData="Search subscriptions..."
          />
          <CustomButton2
            backgroundColor={theme.palette.primary.main}
            className={classes.button}
            onClick={() => {
              navigate('/create-subscription');
            }}>
            <AddIcon
              sx={{
                marginRight: '5px',
              }}
            />{' '}
            Create Subscription
          </CustomButton2>
        </Box>
        <CustomizedTable
          heading="All Subscriptions"
          loading={loading}
          columns={columns}
          page={page}
          limit={pageSize}
          handleLimit={setPageSize}
          handlePage={setPage}
          data={data}
          totalRecords={totalCount}
          handleSort={setSort}
          sort={sort}
          customizableColumns
          bodyCellPadding="10px 12px"
          headerCellPadding="10px 12px"
        />
      </Box>
    </>
  );
}

export default Subscriptions;
